import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import api from '../../utils/api';

const ADMIN_ROLES = ['super_admin', 'manager', 'assistant'];

export function Avatar({ user, size = 32 }) {
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: isAdmin ? 'linear-gradient(135deg,#f5c400,#c9a000)' : 'linear-gradient(135deg,#2a2400,#1e1800)', border: `2px solid ${isAdmin ? '#f5c400' : '#2a2400'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: size * 0.38, color: isAdmin ? '#0a0900' : '#f5c400', flexShrink: 0, overflow: 'hidden' }}>
      {user?.profile?.avatar
        ? <img src={user.profile.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : user?.username?.[0]?.toUpperCase() || '?'}
    </div>
  );
}

export function MessageBubble({ msg, currentUserId }) {
  const isMe = msg.sender?._id === currentUserId || msg.sender?._id === currentUserId;
  const isAdmin = ADMIN_ROLES.includes(msg.sender?.role);
  if (msg.isSystem) return (
    <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', padding: '4px 0', fontStyle: 'italic' }}>— {msg.content} —</div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: isMe ? 'row-reverse' : 'row', gap: 8, marginBottom: 12, alignItems: 'flex-end' }}>
      {!isMe && <Avatar user={msg.sender} size={30} />}
      <div style={{ maxWidth: '72%' }}>
        {!isMe && (
          <div style={{ fontSize: 11, marginBottom: 3, paddingLeft: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ color: isAdmin ? '#f5c400' : 'var(--text-secondary)', fontWeight: 700 }}>{msg.sender?.username}</span>
            {isAdmin && <span style={{ background: '#f5c400', color: '#0a0900', fontSize: 9, padding: '1px 5px', borderRadius: 4, fontWeight: 900 }}>★ ADMIN</span>}
          </div>
        )}
        <div style={{ background: isMe ? 'linear-gradient(135deg,#f5c400,#c9a000)' : 'var(--bg-secondary)', color: isMe ? '#0a0900' : 'var(--text-primary)', padding: '10px 14px', borderRadius: isMe ? '16px 4px 16px 16px' : '4px 16px 16px 16px', fontSize: 14, wordBreak: 'break-word', border: isMe ? 'none' : '1px solid var(--border)' }}>
          {msg.content && <p style={{ margin: 0, lineHeight: 1.5 }}>{msg.content}</p>}
          {msg.image && <img src={msg.image} alt="img" style={{ maxWidth: '100%', borderRadius: 8, marginTop: msg.content ? 8 : 0, display: 'block', cursor: 'pointer' }} onClick={() => window.open(msg.image, '_blank')} />}
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3, textAlign: isMe ? 'right' : 'left', padding: '0 4px' }}>
          {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
}

export function ChatInput({ onSend, placeholder = 'Type a message...', disabled = false, compact = false }) {
  const [text, setText] = useState('');
  const [imageData, setImageData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileRef = useRef();

  const handleImage = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setImageData(ev.target.result); setImagePreview(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const send = () => {
    if ((!text.trim() && !imageData) || disabled) return;
    onSend({ content: text.trim(), image: imageData });
    setText(''); setImageData(null); setImagePreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: compact ? '8px 12px' : '12px 16px', background: 'var(--bg-secondary)', flexShrink: 0 }}>
      {imagePreview && (
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: 8 }}>
          <img src={imagePreview} alt="preview" style={{ height: 60, borderRadius: 8, border: '1px solid var(--border)' }} />
          <button onClick={() => { setImageData(null); setImagePreview(null); }} style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', border: 'none', color: '#fff', width: 18, height: 18, borderRadius: '50%', cursor: 'pointer', fontSize: 11 }}>✕</button>
        </div>
      )}
      {disabled ? (
        <div style={{ padding: '8px 12px', fontSize: 13, color: '#ef4444', background: 'rgba(239,68,68,0.08)', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)', textAlign: 'center' }}>🚫 General chat is disabled</div>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          {!compact && (
            <button onClick={() => fileRef.current?.click()} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 11px', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 17, flexShrink: 0 }}>🖼</button>
          )}
          <input type="file" ref={fileRef} accept="image/*" onChange={handleImage} style={{ display: 'none' }} />
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={placeholder}
            style={{ flex: 1, padding: compact ? '8px 10px' : '10px 12px', fontSize: 13, borderRadius: 8 }}
          />
          <button onClick={send} disabled={!text.trim() && !imageData}
            style={{ background: 'linear-gradient(135deg,#f5c400,#c9a000)', border: 'none', borderRadius: 8, padding: compact ? '8px 12px' : '10px 14px', color: '#0a0900', fontWeight: 700, cursor: (!text.trim() && !imageData) ? 'not-allowed' : 'pointer', fontSize: 15, opacity: (!text.trim() && !imageData) ? 0.5 : 1, flexShrink: 0 }}>➤</button>
        </div>
      )}
    </div>
  );
}

// ── General Room (reused everywhere) ──
export function GeneralRoom({ socket, currentUser, compact = false }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generalEnabled, setGeneralEnabled] = useState(true);
  const [error, setError] = useState('');
  const bottomRef = useRef();
  const isAdmin = ADMIN_ROLES.includes(currentUser?.role);

  useEffect(() => {
    api.get('/chat/general').then(({ data }) => setMessages(data.messages || [])).finally(() => setLoading(false));
    api.get('/chat/general/status').then(({ data }) => setGeneralEnabled(data.enabled));
  }, []);

  useEffect(() => {
    if (!socket) return;
    socket.emit('join_general');
    const onMsg = (msg) => setMessages(p => [...p, msg]);
    const onStatus = ({ enabled, changedBy }) => {
      setGeneralEnabled(enabled);
      setMessages(p => [...p, { _id: Date.now(), isSystem: true, content: `General chat ${enabled ? 'enabled ✅' : 'disabled 🔒'} by ${changedBy}`, createdAt: new Date() }]);
    };
    const onErr = ({ message }) => { setError(message); setTimeout(() => setError(''), 4000); };
    socket.on('new_general_message', onMsg);
    socket.on('general_chat_status', onStatus);
    socket.on('chat_error', onErr);
    return () => { socket.off('new_general_message', onMsg); socket.off('general_chat_status', onStatus); socket.off('chat_error', onErr); };
  }, [socket]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMsg = useCallback(({ content, image }) => {
    if (!socket) return;
    if (image) {
      socket.emit('upload_image', { base64: image, room: 'general' });
      socket.once('image_ready', ({ imageUrl }) => socket.emit('send_general', { content, image: imageUrl }));
    } else {
      socket.emit('send_general', { content, image: '' });
    }
  }, [socket]);

  const canChat = isAdmin || generalEnabled;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {!compact && isAdmin && (
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-secondary)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: generalEnabled ? '#10b981' : '#ef4444' }} />
            <span style={{ fontSize: 13, color: generalEnabled ? '#10b981' : '#ef4444', fontWeight: 700 }}>{generalEnabled ? 'Chat Open' : 'Chat Closed'}</span>
          </div>
          <button onClick={() => socket?.emit('toggle_general_chat')} className={`btn btn-sm ${generalEnabled ? 'btn-danger' : 'btn-primary'}`} style={{ fontSize: 12 }}>
            {generalEnabled ? '🔒 Disable' : '🔓 Enable'}
          </button>
        </div>
      )}
      {error && <div style={{ padding: '6px 16px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: 12, borderBottom: '1px solid rgba(239,68,68,0.2)' }}>{error}</div>}
      <div style={{ flex: 1, overflowY: 'auto', padding: compact ? '10px 12px' : '16px' }}>
        {loading ? <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div> :
          messages.length === 0 ? <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 30, fontSize: 13 }}>No messages yet. Say hello! 👋</div> :
          messages.map(m => <MessageBubble key={m._id} msg={m} currentUserId={currentUser?.id || currentUser?._id} />)}
        <div ref={bottomRef} />
      </div>
      <ChatInput onSend={sendMsg} placeholder="Chat with everyone..." disabled={!canChat} compact={compact} />
    </div>
  );
}

// ── Private Chat (reused everywhere) ──
export function PrivateChat({ socket, targetUser, currentUser, compact = false }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef();

  useEffect(() => {
    if (!targetUser?._id) return;
    setLoading(true);
    api.get(`/chat/private/${targetUser._id}`).then(({ data }) => setMessages(data.messages || [])).finally(() => setLoading(false));
  }, [targetUser]);

  useEffect(() => {
    if (!socket || !targetUser?._id) return;
    socket.emit('join_private', { targetUserId: targetUser._id });
    const handler = (msg) => setMessages(p => [...p, msg]);
    socket.on('new_private_message', handler);
    return () => socket.off('new_private_message', handler);
  }, [socket, targetUser]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMsg = useCallback(({ content, image }) => {
    if (!socket || !targetUser?._id) return;
    if (image) {
      socket.emit('upload_image', { base64: image, room: 'private', targetUserId: targetUser._id });
      socket.once('image_ready', ({ imageUrl }) => socket.emit('send_private', { targetUserId: targetUser._id, content, image: imageUrl }));
    } else {
      socket.emit('send_private', { targetUserId: targetUser._id, content, image: '' });
    }
  }, [socket, targetUser]);

  const isAdmin = ADMIN_ROLES.includes(targetUser?.role);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg-secondary)', flexShrink: 0 }}>
        <Avatar user={targetUser} size={34} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 7 }}>
            {targetUser?.username}
            {isAdmin && <span style={{ background: '#f5c400', color: '#0a0900', fontSize: 9, padding: '1px 6px', borderRadius: 4, fontWeight: 900 }}>★ SUPPORT</span>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Private conversation · Messages are saved</div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: compact ? '10px 12px' : '16px' }}>
        {loading ? <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div> :
          messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 30 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>💬</div>
              <p style={{ fontSize: 13 }}>Start the conversation!<br /><span style={{ fontSize: 11 }}>Messages are saved — admin will reply when available.</span></p>
            </div>
          ) : messages.map(m => <MessageBubble key={m._id} msg={m} currentUserId={currentUser?.id || currentUser?._id} />)}
        <div ref={bottomRef} />
      </div>
      <ChatInput onSend={sendMsg} placeholder={`Message ${targetUser?.username}...`} compact={compact} />
    </div>
  );
}

// ── Agent selector for users ──
export function UserSupportPanel({ socket, currentUser, compact = false }) {
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/chat/agents').then(({ data }) => {
      setAgents(data.agents || []);
      if (data.agents?.length) setSelected(data.agents[0]);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}><div className="spinner" /></div>;

  if (agents.length === 0) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 12, padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: 40 }}>🎧</div>
      <p style={{ fontSize: 14 }}>No support agents available.<br /><span style={{ fontSize: 12 }}>Please try again later.</span></p>
    </div>
  );

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden', flexDirection: 'column', height: '100%' }}>
      {agents.length > 1 && (
        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, overflowX: 'auto', flexShrink: 0 }}>
          {agents.map(a => (
            <button key={a._id} onClick={() => setSelected(a)} style={{ padding: '5px 12px', borderRadius: 20, border: `1px solid ${selected?._id === a._id ? '#f5c400' : 'var(--border)'}`, background: selected?._id === a._id ? 'rgba(245,196,0,0.1)' : 'transparent', color: selected?._id === a._id ? '#f5c400' : 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {a.username}
            </button>
          ))}
        </div>
      )}
      {selected
        ? <PrivateChat socket={socket} targetUser={selected} currentUser={currentUser} compact={compact} />
        : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'var(--text-muted)' }}>Select an agent</div>}
    </div>
  );
}

// ── Admin Support Inbox ──
export function AdminSupportInbox({ socket, currentUser }) {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get('/chat/conversations').then(({ data }) => setConversations(data.conversations || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!socket) return;
    socket.on('new_private_message', load);
    return () => socket.off('new_private_message', load);
  }, [socket]);

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden', height: '100%' }}>
      <div style={{ width: 260, borderRight: '1px solid var(--border)', background: 'var(--bg-secondary)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-display)', letterSpacing: '0.08em' }}>SUPPORT INBOX</div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div> :
            conversations.length === 0 ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No conversations yet</div> :
            conversations.map(conv => (
              <div key={conv.user?._id} onClick={() => setSelected(conv.user)}
                style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', background: selected?._id === conv.user?._id ? 'rgba(245,196,0,0.08)' : 'transparent', display: 'flex', alignItems: 'center', gap: 9, transition: 'background 0.15s' }}>
                <Avatar user={conv.user} size={34} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{conv.user?.username}</span>
                    {conv.unread > 0 && <span style={{ background: '#f5c400', color: '#0a0900', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 900 }}>{conv.unread}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {conv.lastMessage?.image && !conv.lastMessage?.content ? '📷 Image' : conv.lastMessage?.content || '...'}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selected
          ? <PrivateChat socket={socket} targetUser={selected} currentUser={currentUser} />
          : <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 48 }}>📬</div>
              <p>Select a conversation to reply</p>
            </div>}
      </div>
    </div>
  );
}
